import React, { useEffect, useState, createContext, useContext } from 'react';
// ─── Centralized Pricing Config (Single Source of Truth) ───
interface CountryPricing {
  current: number;
  compareAt: number;
  currency: string;
  locale: string;
  // Whether this currency uses decimals in display
  decimals: boolean;
}
const PRICING_CONFIG: Record<string, CountryPricing> = {
  CL: {
    current: 14999,
    compareAt: 43999,
    currency: 'CLP',
    locale: 'es-CL',
    decimals: false
  },
  AR: {
    current: 23999,
    compareAt: 70999,
    currency: 'ARS',
    locale: 'es-AR',
    decimals: false
  },
  CO: {
    current: 63999,
    compareAt: 189999,
    currency: 'COP',
    locale: 'es-CO',
    decimals: false
  },
  MX: {
    current: 299,
    compareAt: 899,
    currency: 'MXN',
    locale: 'es-MX',
    decimals: false
  },
  UY: {
    current: 670,
    compareAt: 1999,
    currency: 'UYU',
    locale: 'es-UY',
    decimals: false
  },
  ES: {
    current: 14.99,
    compareAt: 42.99,
    currency: 'EUR',
    locale: 'es-ES',
    decimals: true
  },
  US: {
    current: 16.99,
    compareAt: 49.99,
    currency: 'USD',
    locale: 'en-US',
    decimals: true
  }
};
const DEFAULT_PRICING = PRICING_CONFIG['US'];
// Base USD values for ratio calculations (value stack items)
const BASE_USD_CURRENT = 16.99;
const BASE_USD_COMPARE = 49.99;
// ─── Formatting ───
function formatAmount(amount: number, pricing: CountryPricing): string {
  if (!pricing.decimals) {
    // CLP, ARS, COP, UYU, MXN — use dot as thousands separator, no decimals
    const rounded = Math.round(amount);
    // Format with dot thousands separator
    return rounded.toLocaleString('de-DE'); // de-DE uses dots for thousands
  }
  // EUR uses comma decimal
  if (pricing.currency === 'EUR') {
    return amount.toFixed(2).replace('.', ',');
  }
  // USD, others with decimals
  return amount.toFixed(2);
}
export function formatPrice(amount: number, pricing: CountryPricing): string {
  return `${formatAmount(amount, pricing)} ${pricing.currency}`;
}
// Convert a USD amount to the local currency using the ratio
export function convertFromUSD(usdAmount: number, pricing: CountryPricing): number {
  const ratio = pricing.current / BASE_USD_CURRENT;
  return usdAmount * ratio;
}
// Convert a USD compare-at amount to local currency
export function convertCompareFromUSD(usdAmount: number, pricing: CountryPricing): number {
  const ratio = pricing.compareAt / BASE_USD_COMPARE;
  return usdAmount * ratio;
}
// ─── Geo Detection ───
async function detectCountry(): Promise<string> {
  // Check sessionStorage cache first
  if (typeof window !== 'undefined') {
    const cached = sessionStorage.getItem('geo_country');
    if (cached) return cached;
  }
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const res = await fetch('https://ipapi.co/json/', {
      signal: controller.signal
    });
    clearTimeout(timeout);
    if (res.ok) {
      const data = await res.json();
      const country = data.country_code || 'US';
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('geo_country', country);
      }
      return country;
    }
  } catch {
    // Primary API failed, try fallback
  }
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const res = await fetch('https://ip-api.com/json/?fields=countryCode', {
      signal: controller.signal
    });
    clearTimeout(timeout);
    if (res.ok) {
      const data = await res.json();
      const country = data.countryCode || 'US';
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('geo_country', country);
      }
      return country;
    }
  } catch {
    // Both APIs failed
  }
  return 'US';
}
// ─── React Context ───
interface PricingContextValue {
  pricing: CountryPricing;
  countryCode: string;
  isLoading: boolean;
  formatPrice: (amount: number) => string;
  formatComparePrice: (amount: number) => string;
  convertFromUSD: (usdAmount: number) => number;
  convertCompareFromUSD: (usdAmount: number) => number;
}
const PricingContext = createContext<PricingContextValue>({
  pricing: DEFAULT_PRICING,
  countryCode: 'US',
  isLoading: true,
  formatPrice: amount => formatPrice(amount, DEFAULT_PRICING),
  formatComparePrice: amount => formatPrice(amount, DEFAULT_PRICING),
  convertFromUSD: usd => usd,
  convertCompareFromUSD: usd => usd
});
export function PricingProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [countryCode, setCountryCode] = useState('US');
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    detectCountry().then(code => {
      setCountryCode(code);
      setIsLoading(false);
    });
  }, []);
  const pricing = PRICING_CONFIG[countryCode] || DEFAULT_PRICING;
  const value: PricingContextValue = {
    pricing,
    countryCode,
    isLoading,
    formatPrice: (amount: number) => formatPrice(amount, pricing),
    formatComparePrice: (amount: number) => formatPrice(amount, pricing),
    convertFromUSD: (usd: number) => convertFromUSD(usd, pricing),
    convertCompareFromUSD: (usd: number) => convertCompareFromUSD(usd, pricing)
  };
  return <PricingContext.Provider value={value} data-id="element-408">{children}</PricingContext.Provider>;
}
export function useGeoPrice() {
  return useContext(PricingContext);
}